// eslint-disable-next-line @typescript-eslint/no-unused-vars -- InfoIcon used in JSX
import { CloseIcon, InfoIcon } from '@chakra-ui/icons';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Tooltip used in JSX
import { Box, Flex, Radio, RadioGroup, Stack, Text, Tooltip } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import {
  setActionCreateForm,
  clearStepperFormData,
  setClearSelectedAreasTable,
} from 'redux/DataImportSlice';

import { checkedRadioStyles } from '../Helper';

import DimensionType from './ActionTwoSteps/DimensionType';
import DataType from './ActionTwoSteps/DataType';

interface Properties {
  setHelpModalSeleceted: any;
  flags?: any;
}

/** Returns the step-2 panel heading based on the selected import type. */
function stepTwoHeading(dataType: string | null): string {
  if (dataType === '1') return 'Set up your dimension import';
  if (dataType === '2') return 'Set up your actuals import';
  if (dataType === '3') return 'Set up your forecast import';
  return 'Where do you want to load this data?';
}

/**
 * Step 2 of the import wizard — lets the user pick the import type (dimension / actuals / forecast)
 * then renders the matching sub-step form.
 *
 * Note: Several JSX lines carry `@ts-ignore` because Chakra UI builds very deep prop union types.
 * When composed with `withLDConsumer` TypeScript exceeds its union-instantiation limit (TS2589).
 * The ignores are intentionally narrow and do not mask real type errors.
 */
const ActionStepTwo = ({ setHelpModalSeleceted, flags }: Properties) => {
  const dispatch: any = useDispatch();

  /** Narrow selector avoids inferring the whole form object, reducing TS union depth. */
  const actionFormDataType = useSelector(
    (state: any) => state.dataImportSlice.actionFormData.dataType as string | undefined,
  );

  const [dataType, setDataType] = useState<string | null>(() => {
    const value = actionFormDataType;
    return value === '' || value === undefined || value === null ? null : value;
  });

  // Edit flow: step may mount before getOneActionData fills Redux — keep radio in sync.
  useEffect(() => {
    const value = actionFormDataType;
    if (value === '1' || value === '2' || value === '3') {
      setDataType(value);
    } else if (value === '' || value === undefined || value === null) {
      setDataType(null);
    }
  }, [actionFormDataType]);

  const handleCloseStep = async () => {
    await dispatch(setActionCreateForm(false));
    await dispatch(clearStepperFormData());
    await dispatch(setClearSelectedAreasTable());
  };

  return (
    <Box width='100%' pt={3}>
      {/* @ts-ignore TS2589: Chakra Flex union depth + withLDConsumer exceeds TS threshold */}
      <Flex justifyContent='space-between' w='full' align='center'>
        <Text
          color='#6562CF'
          fontSize='lg'
          fontFamily='regular_roboto'
          fontWeight='400'
          textAlign='start'
        >
          {stepTwoHeading(dataType)}
        </Text>
        <CloseIcon color='black' width={3} height={3} cursor='pointer' onClick={handleCloseStep} />
      </Flex>

      <Box display='flex' flexDirection='column' justifyContent='center' width='100%'>
        <Box display='flex' flexDirection='column' justifyContent='center' width='100%'>
          <RadioGroup
            width='100%'
            pt={3}
            onChange={(value) => {
              setDataType(value);
            }}
            value={dataType ?? undefined}
          >
            <Stack spacing={3} direction='column'>
              <Radio _checked={checkedRadioStyles} value='1'>
                <Flex align='center' gap={2}>
                  <Text color='black' fontSize='sm' fontFamily='regular_roboto' fontWeight={600}>
                    Load dimension items
                  </Text>
                  <Tooltip
                    label='Add, update or remove items in a dimension — e.g. employees, cost centres.'
                    hasArrow
                    placement='top'
                  >
                    <Flex as='span' display='inline-flex' cursor='help'>
                      <InfoIcon color='gray.500' boxSize={3.5} />
                    </Flex>
                  </Tooltip>
                </Flex>
              </Radio>
              {flags?.importAsDataType && (
                <Radio _checked={checkedRadioStyles} value='2'>
                  <Flex align='center' gap={2}>
                    <Text color='black' fontSize='sm' fontFamily='regular_roboto' fontWeight={600}>
                      Load actual data
                    </Text>
                    <Tooltip
                      label='Import actual values from an external system into your model.'
                      hasArrow
                      placement='top'
                    >
                      <Flex as='span' display='inline-flex' cursor='help'>
                        <InfoIcon color='gray.500' boxSize={3.5} />
                      </Flex>
                    </Tooltip>
                  </Flex>
                </Radio>
              )}
              {flags?.importAsForecastType && (
                <Radio _checked={checkedRadioStyles} value='3'>
                  <Flex align='center' gap={2}>
                    <Text color='black' fontSize='sm' fontFamily='regular_roboto' fontWeight={600}>
                      Load forecast data
                    </Text>
                    <Tooltip
                      label='Import forecast values into a scenario. Only loads data to the forecast, not actuals.'
                      hasArrow
                      placement='top'
                    >
                      <Flex as='span' display='inline-flex' cursor='help'>
                        <InfoIcon color='gray.500' boxSize={3.5} />
                      </Flex>
                    </Tooltip>
                  </Flex>
                </Radio>
              )}
            </Stack>
          </RadioGroup>
          {dataType === '1' ? (
            <DimensionType setHelpModalSeleceted={setHelpModalSeleceted} dataType={dataType} />
          ) : dataType === '2' || dataType === '3' ? (
            <DataType setHelpModalSeleceted={setHelpModalSeleceted} dataType={dataType} />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default withLDConsumer()(ActionStepTwo);
